import { NodeTypes, type Input } from "@/workflow_api/types/workflow";
import type { InteroperabilityConfiguration, ParameterMapping } from "../types";
import type { NodeVariable } from "../../types";

/**
 * Mapea el tipo de parámetro del servicio al tipo del backend
 */
function mapParameterTypeToBackend(paramType: string): string {
  switch (paramType.toLowerCase()) {
    case "string":
    case "text":
      return "STRING";
    case "number":
    case "integer":
    case "int":
      return "INTEGER";
    case "float":
    case "decimal":
      return "NUMBER";
    case "boolean":
    case "bool":
      return "BOOLEAN";
    case "date":
    case "datetime":
      return "DATE";
    case "array":
      return "ARRAY";
    default:
      return "STRING";
  }
}

/**
 * Maps variable type from frontend to backend type
 */
function mapVariableTypeToBackend(variableType: string): string {
  switch (variableType) {
    case "string":
      return "STRING";
    case "number":
      return "INTEGER";
    case "boolean":
      return "BOOLEAN";
    case "date":
      return "DATE";
    case "array":
      return "ARRAY";
    default:
      return "STRING";
  }
}

/**
 * Helper function to get variable connection info by name
 * Returns nodeId, variableId, variableType, and fieldInfo for a variable found by name
 */
function getVariableConnectionByName(
  variableName: string,
  variables?: NodeVariable[],
): {
  nodeId: string;
  variableId: string;
  variableType: string;
  isSwitchVariable: boolean;
  fieldInfo?: { id: string; name: string; type: string };
} | null {
  if (!variableName || !variables) return null;

  for (const nodeVar of variables) {
    const variable = nodeVar.variables.find((v) => v.name === variableName);
    if (variable) {
      // Check if it's a switch variable (where variable.id === variable.node_id)
      const isSwitchVariable = variable.id === variable.node_id;

      const result: {
        nodeId: string;
        variableId: string;
        variableType: string;
        isSwitchVariable: boolean;
        fieldInfo?: { id: string; name: string; type: string };
      } = {
        nodeId: variable.node_id || nodeVar.nodeId,
        variableId: variable.id,
        variableType: variable.type,
        isSwitchVariable,
      };

      if (!isSwitchVariable) {
        result.fieldInfo = {
          id: variable.id,
          name: variable.name,
          type: variable.type,
        };
      }

      return result;
    }
  }
  return null;
}

/**
 * Helper function to check if a value is a variable name
 */
function isVariableName(value: string, variables?: NodeVariable[]): boolean {
  if (!value || !variables) return false;
  return variables.some((node) => node.variables.some((v) => v.name === value));
}

/**
 * Serializa la configuración de interoperabilidad a inputs del nodo
 * Los inputs deben seguir el formato:
 * - service: STRING (cnr | rnpn)
 * - action: STRING (endpointId)
 * - Para cada parámetro: un input con name=parameterId, value/connection según corresponda
 */
export function serializeInteroperabilityNode(
  config: InteroperabilityConfiguration,
  variables?: NodeVariable[],
): Input {
  const inputs: Input = [];

  // Input 1: service (STRING)
  inputs.push({
    name: "service",
    value: config.serviceId,
    type: "STRING",
  });

  // Input 2: action (STRING) - el endpointId
  inputs.push({
    name: "action",
    value: config.endpointId,
    type: "STRING",
  });

  // Inputs para cada parámetro
  // Filtrar parámetros con valores vacíos (similar a notificaciones)
  const validMappings = config.inputMappings.filter(
    (mapping) => mapping.variableName && mapping.variableName.trim() !== "",
  );

  validMappings.forEach((mapping: ParameterMapping) => {
    // Determinar si es una variable:
    // 1. Si variableId existe y no está vacío, es una variable (formato antiguo o ya resuelto)
    // 2. Si variableName es un nombre de variable válido, es una variable
    const hasVariableId =
      !!mapping.variableId && mapping.variableId.trim() !== "";
    const isVariableByName = isVariableName(mapping.variableName, variables);
    const isVariable = hasVariableId || isVariableByName;

    console.log(isVariableByName, isVariable);

    if (isVariable) {
      // Es una variable conectada
      // Get connection info by variableId first (more reliable), then by name
      let connectionInfo: {
        nodeId: string;
        variableId: string;
        variableType: string;
        isSwitchVariable: boolean;
        fieldInfo?: { id: string; name: string; type: string };
        output_name?: string;
        ref_name?: string;
      } | null = null;

      // Prioritize variableId if available (more reliable)
      if (mapping.variableId && mapping.variableId.trim() !== "") {
        // Search for the variable by ID to get connection info
        if (variables) {
          for (const nodeVar of variables) {
            const variable = nodeVar.variables.find(
              (v) => v.id === mapping.variableId,
            );

            if (variable) {
              const isSwitchVariable = variable.id === variable.node_id;
              connectionInfo = {
                nodeId: variable.node_id || nodeVar.nodeId,
                variableId: variable.id,
                variableType: variable.type,
                output_name: variable.output_name,
                isSwitchVariable,
                ref_name: nodeVar.ref_name,
                fieldInfo: !isSwitchVariable
                  ? {
                      id: variable.id,
                      name: variable.name,
                      type: variable.type,
                    }
                  : undefined,
              };
              break;
            }
          }
        }
      }

      // If not found by ID, try to find by name
      if (!connectionInfo && isVariableByName) {
        connectionInfo = getVariableConnectionByName(
          mapping.variableName,
          variables,
        );
      }

      if (connectionInfo) {
        const simpleInput: Input[number] = {
          name: mapping.parameterId,
          type: mapVariableTypeToBackend(connectionInfo.variableType),
          value: null,
        };

        let isGlobalVariable = false;
        let globalType:
          | "APPLICANT"
          | "NODE_EXECUTION_START"
          | "NODE_EXECUTION_FINISH"
          | "CURRENT_TIME"
          | undefined;

        for (const nodeVar of variables || []) {
          const variable = nodeVar.variables.find(
            (v) => v.id === connectionInfo.variableId,
          );
          if (variable && variable.global_type) {
            isGlobalVariable = true;
            globalType = variable.global_type;
            break;
          }
        }

        if (isGlobalVariable && globalType) {
          simpleInput.value = connectionInfo.nodeId;
          simpleInput.global_type = globalType;
        } else {
          simpleInput.connection = connectionInfo.nodeId;
          if (
            connectionInfo.ref_name === NodeTypes.N8N ||
            connectionInfo.ref_name === NodeTypes.INTEROPERABILITY
          ) {
            simpleInput.output_name = connectionInfo.output_name;
            simpleInput.connection = connectionInfo.nodeId;
          } else {
            if (!connectionInfo.isSwitchVariable && connectionInfo.fieldInfo) {
              simpleInput.field = connectionInfo.variableId;
              simpleInput.field_id = connectionInfo.variableId;
              simpleInput.source_connections = [
                {
                  field: connectionInfo.fieldInfo,
                  source_node_id: connectionInfo.nodeId,
                  field_id: connectionInfo.variableId,
                },
              ];
            }
          }
        }

        inputs.push(simpleInput);
      } else {
        console.warn(
          `[serializeInteroperabilityNode] Variable ID not found for name: ${mapping.variableName}`,
        );
      }
    } else {
      // Es un valor directo
      // Convertir el valor al tipo adecuado si es necesario
      let typedValue: string | number | boolean | null =
        mapping.variableName || null;

      // Intentar convertir según el tipo
      if (typedValue !== null && typeof typedValue === "string") {
        const backendType = mapParameterTypeToBackend(mapping.variableType);
        if (
          backendType === "INTEGER" ||
          backendType === "NUMBER" ||
          backendType === "FLOAT"
        ) {
          const numValue = Number(typedValue);
          if (!isNaN(numValue)) {
            typedValue = numValue;
          }
        } else if (backendType === "BOOLEAN") {
          typedValue = typedValue === "true" || typedValue === "1";
        }
      }

      const simpleInput: Input[number] = {
        name: mapping.parameterId,
        type: mapParameterTypeToBackend(mapping.variableType),
        value: typedValue,
      };
      inputs.push(simpleInput);
    }
  });

  return inputs;
}
